/**
 * The work context a cached knowledge answer was produced under, as query-key
 * data (ADR 0091 D3). A contextual key puts the organisation at index 2 and the
 * actor at index 3 and ENDS with its context segment, spread as separate
 * elements (never inside `stableStringify`), so the cache can be walked by work
 * item and tuple without knowing each family's shape. An office key has no
 * segment at all and stays exactly what it was before work contexts existed.
 * See docs/plans/2026-10-20-feat-knowledge-spaces-frontend-context-scoping-plan.md
 * ("Contract for sibling parts").
 */
import type { KnowledgeWorkItem, KnowledgeWorkItemKind } from '@/hooks/use-knowledge-work-context';

export type KnowledgeContextScope =
  | { kind: 'office' }
  | { kind: 'work'; workItemKey: string; engagementId: string | null; revision: number };

export const OFFICE_CONTEXT_SCOPE: KnowledgeContextScope = Object.freeze({ kind: 'office' });

/** Opens every context segment; a key whose tail does not start with it is not contextual. */
export const CONTEXT_SEGMENT_MARKER = 'work-scope';
/** The tuple of a key built while the work item had no resolution to name. */
export const UNRESOLVED_CONTEXT_TUPLE = 'unresolved';
/** Stands for a null engagement (office material) inside a segment; engagement ids are uuids. */
const OFFICE_ENGAGEMENT = 'office';

const WORK_ITEM_KINDS: ReadonlySet<string> = new Set<KnowledgeWorkItemKind>([
  'draft',
  'correspondence',
  'conversation',
]);

/** `kind:id`; the work item, never an engagement, is the boundary (ADR 0091). */
export function workItemKey(item: KnowledgeWorkItem): string {
  return `${item.kind}:${item.id}`;
}

/** The work item a `workItemKey` names, or null for anything else. */
export function parseWorkItemKey(key: string): KnowledgeWorkItem | null {
  const separator = key.indexOf(':');
  if (separator <= 0 || separator === key.length - 1) {
    return null;
  }
  const kind = key.slice(0, separator);
  if (!WORK_ITEM_KINDS.has(kind)) {
    return null;
  }
  return { kind: kind as KnowledgeWorkItemKind, id: key.slice(separator + 1) };
}

/** The comparable form of a resolved engagement and revision. */
export function contextTuple(engagementId: string | null, revision: number): string {
  return `${engagementId ?? OFFICE_ENGAGEMENT}#${revision}`;
}

/** Empty for office scope, so an office key keeps its pre-context shape. */
export function contextScopeSegment(scope: KnowledgeContextScope): readonly (string | number)[] {
  if (scope.kind === 'office') {
    return [];
  }
  return [
    CONTEXT_SEGMENT_MARKER,
    scope.workItemKey,
    scope.engagementId ?? OFFICE_ENGAGEMENT,
    scope.revision,
  ];
}

/** The segment of a key built while its work item is pending or blocked; never fetched. */
export function unresolvedSegment(key: string): readonly string[] {
  return [CONTEXT_SEGMENT_MARKER, key, UNRESOLVED_CONTEXT_TUPLE];
}

/**
 * The work item and tuple a key's trailing segment names, or null for a key
 * without one. Read from the tail: a query text earlier in the key may hold
 * any string, the marker included.
 */
export function readContextSegment(
  key: readonly unknown[],
): { workItemKey: string; tuple: string } | null {
  const n = key.length;
  const last = key[n - 1];
  const third = key[n - 3];
  if (
    n >= 3 &&
    third === CONTEXT_SEGMENT_MARKER &&
    last === UNRESOLVED_CONTEXT_TUPLE &&
    typeof key[n - 2] === 'string'
  ) {
    return { workItemKey: key[n - 2] as string, tuple: UNRESOLVED_CONTEXT_TUPLE };
  }
  const engagement = key[n - 2];
  if (
    n >= 4 &&
    key[n - 4] === CONTEXT_SEGMENT_MARKER &&
    typeof third === 'string' &&
    typeof engagement === 'string' &&
    typeof last === 'number'
  ) {
    return { workItemKey: third, tuple: `${engagement}#${last}` };
  }
  return null;
}

/** A contextual read whose key no longer names the work item's current resolution. */
export class ObsoleteKnowledgeContextError extends Error {
  constructor() {
    super('knowledge_context_obsolete');
    this.name = 'ObsoleteKnowledgeContextError';
  }
}

/** The server did not confirm the tuple the key names (absent or different echo, or a 409). */
export class KnowledgeContextMismatchError extends Error {
  constructor() {
    super('knowledge_context_mismatch');
    this.name = 'KnowledgeContextMismatchError';
  }
}

/** Neither context error is retried: a retry would ask under the same tuple again. */
export function isKnowledgeContextError(error: unknown): boolean {
  return (
    error instanceof ObsoleteKnowledgeContextError || error instanceof KnowledgeContextMismatchError
  );
}
